import { Api } from './';

/**
 * The Email Manager's endpoints, in one place.
 *
 * The generic `Api` wrapper handles auth headers, loading state and error surfacing; what it does
 * not do is remember paths. Eight components need these, and eight copies of `'/email/queryAudience'`
 * is eight places for a rename to be missed. Nothing here calls `axios` directly — the wrapper is
 * the only HTTP path in the CMS.
 */
const emailApi = {
  // --- Templates ---
  /*
    `{ silent: true }` on the two template reads.

    On an environment where `scripts/seedEmailTemplates.ts` has not run, `EmailTemplate` is empty
    and both of these answer 404 `No template named "campaign"`. The screen loads them on mount, so
    without this an operator opening the Email Manager is met by an error toast for a request they
    never made — and a toast is the wrong place to say "this environment needs seeding". The screen
    renders that as an inline state instead.

    Silent returns `{ err, status }` rather than throwing, so the caller can tell 404 from a real
    failure.
  */
  listTemplates: () => Api.get('/email/listTemplates', null, { silent: true }),

  getTemplate: (key) =>
    Api.get(`/email/getTemplate?key=${encodeURIComponent(key)}`, null, { silent: true }),

  updateTemplateText: (key, blocks, button) =>
    Api.post('/email/updateTemplateText', { key, blocks }, button, { silent: true }),

  listTemplateVersions: (key, cursor) =>
    Api.get(
      `/email/listTemplateVersions?key=${encodeURIComponent(key)}${cursor ? `&cursor=${cursor}` : ''}`
    ),

  restoreTemplateVersion: (versionId, button) =>
    Api.post('/email/restoreTemplateVersion', { versionId }, button),

  /*
    The destination is passed because the endpoint renders the button with it.

    It accepted `destination` and `destinationTarget` all along and this wrapper never sent them, so
    every preview drew the campaign's button pointing at the default destination whatever the
    operator had chosen. The one control whose whole purpose is showing the address before a send
    was showing a different one.
  */
  previewTemplate: (
    key,
    blocks,
    sampleVariables,
    destination,
    destinationTarget,
    detailRows,
    /*
      Which language's banner to draw, and nothing else about the email.

      Optional, and omitting it renders the fallback locale — so every existing caller behaves
      exactly as it did. The preview goes through this one endpoint rather than a banner-specific
      one so that what an operator reviews and what a fan receives cannot drift apart.
    */
    locale
  ) =>
    Api.post(
      '/email/previewTemplate',
      { key, blocks, sampleVariables, destination, destinationTarget, detailRows, locale },
      null,
      { silent: true }
    ),

  /*
    The challenges a campaign's button can be pointed at.

    Silent like the other composer reads: the picker says why its list is empty beside the list
    itself, so a network hiccup here is better answered there than by a toast over the page.

    `search` is still in the signature — the composer's own box is gone, but the parameter is the
    endpoint's, and `id` beside it is how a saved campaign's challenge is resolved.
  */
  listFancamChallenges: (search, skip, limit, id) =>
    Api.post('/email/listFancamChallenges', { search, skip, limit, id }, null, { silent: true }),

  // --- Audience ---

  /**
   * Selectable events for the "took part in" filter (FR-029).
   *
   * Two existing admin endpoints rather than a new email-specific one: the backend's audience query
   * resolves this filter against FanWave participation and quiz responses, so those two lists are
   * exactly the set of ids it can match. An endpoint in the email domain would have duplicated them
   * and given the copies somewhere to drift apart.
   *
   * `{ silent: true }` on both, because this is one optional control on a panel of eight. Without
   * it a failing list raises the global error toast, so an operator opening the Email screen to
   * write a campaign would be told about a FanWave request they never made.
   *
   * `allSettled` for the same reason: one list failing still yields the other. Offering fewer
   * events is better than offering none.
   *
   * Waves with a null `waveId` are dropped — they predate waves having identities, so no
   * participation row references them and filtering on one would match nobody.
   */
  listEvents: async () => {
    const [waves, quizzes] = await Promise.allSettled([
      Api.get('/admin/fanwave/list?limit=100', null, { silent: true }),
      Api.get('/admin/quizzes/list?limit=100', null, { silent: true })
    ]);

    const events = [];

    // Envelope is `{ data: { waves, total, page } }`.
    const waveRows = waves.status === 'fulfilled' ? waves.value?.data?.waves : null;
    if (Array.isArray(waveRows)) {
      waveRows.forEach((wave) => {
        if (!wave?.waveId) return;
        const when = wave.startsAt ? new Date(wave.startsAt).toLocaleDateString() : wave.status;
        events.push({ value: wave.waveId, label: `FanWave — ${when}` });
      });
    }

    // Envelope is `{ data: [...], pagination }`.
    const quizRows = quizzes.status === 'fulfilled' ? quizzes.value?.data : null;
    if (Array.isArray(quizRows)) {
      quizRows.forEach((quiz) => {
        if (!quiz?._id) return;
        events.push({
          value: String(quiz._id),
          label: `Quiz — ${(quiz.question || 'Quiz').slice(0, 48)}`
        });
      });
    }

    return events;
  },

  queryAudience: (filters, limit, cursor) =>
    Api.post('/email/queryAudience', { ...filters, limit, cursor }),

  /**
   * The channels a campaign audience can be aimed at.
   *
   * `{ silent: true }` for the same reason `listFancamChallenges` has it: the picker reports an
   * empty list in its own words, right beside the control, which is where an operator is looking.
   *
   * Called with no argument now that the composer has no search box; the parameter stays because
   * it is the endpoint's, not the picker's.
   */
  listCampaignChannels: (search) =>
    Api.post('/email/listCampaignChannels', { search }, null, { silent: true }),

  /**
   * How many fans a channel audience would actually reach.
   *
   * Not `queryAudience`. That one pages the audience table and answers with an explicitly
   * estimated match count; this answers with the final eligible figure the send controls block on,
   * plus why it cannot send when it cannot.
   *
   * Silent because every one of its refusals — no recipients, channel gone, count unavailable —
   * has a place on the builder to be said, beside the audience it is about.
   */
  previewChannelAudience: (channelAudience) =>
    Api.post(
      '/email/previewChannelAudience',
      { audienceMode: 'channelSpecific', channelAudience },
      null,
      { silent: true }
    ),

  /**
   * The accounts a campaign can be aimed at one at a time.
   *
   * Takes a search term where `listCampaignChannels` takes none, and the difference is the size of
   * the set: there are twenty channels worth ranking and twelve thousand accounts, so "the first
   * twenty" is not an answer here. An empty term returns the newest signups, which on a
   * development environment is usually the account the operator just made.
   *
   * `{ silent: true }` like its sibling: the picker reports an empty list in its own words, right
   * beside the control.
   */
  listCampaignRecipients: (search) =>
    Api.post('/email/listCampaignRecipients', { search }, null, { silent: true }),

  /**
   * Whether one named account would actually receive this campaign.
   *
   * A count of one or zero, and which of the two is the point — an unverified or unsubscribed
   * address is invisible from the composer and produces a campaign that reports success and
   * delivers nothing. Answers with the account's current address too, so the screen can show the
   * operator which inbox they are about to mail.
   *
   * Silent for the reason `previewChannelAudience` is: every refusal has a place on the builder to
   * be said, beside the audience it is about.
   */
  previewDirectRecipient: (directRecipient) =>
    Api.post(
      '/email/previewDirectRecipient',
      { audienceMode: 'directRecipient', directRecipient },
      null,
      { silent: true }
    ),

  /**
   * The CSV export is a file download, so it does not go through the JSON wrapper.
   *
   * Returned as a blob and handed to the browser, because the endpoint streams and setting
   * `window.location` would drop the Authorization header the wrapper adds.
   */
  exportAudienceUrl: '/email/exportAudience',

  // --- Campaigns ---
  /*
    `{ silent: true }` on every campaign write below, because each of their refusals has a knowable
    cause and a place on the screen to say it.

    A refusal came back as a six-second red toast in the corner of the window — the wrong end of
    the page from the control that caused it, and gone before an operator finished reading. The
    composer renders these beside the button, the way the test-send 429 already did with its reset
    time.
  */
  createCampaign: (payload, button) =>
    Api.post('/email/createCampaign', payload, button, { silent: true }),

  /**
   * Corrects a draft in place.
   *
   * The composer's save button said "Update Draft" and called `createCampaign`, because there was
   * no update endpoint — so correcting a campaign produced a second one.
   */
  updateCampaign: (campaignId, payload, button) =>
    Api.post('/email/updateCampaign', { campaignId, ...payload }, button, { silent: true }),

  listCampaigns: ({ from, to, status, includeArchived, recurringParent } = {}) => {
    const query = new URLSearchParams();
    if (from) query.set('from', from);
    if (to) query.set('to', to);
    if (status) query.set('status', status);
    // Archived campaigns are hidden by default, server-side. Asking for them is opt-in.
    if (includeArchived) query.set('includeArchived', 'true');
    if (recurringParent) query.set('recurringParent', recurringParent);
    const suffix = query.toString();
    return Api.get(`/email/listCampaigns${suffix ? `?${suffix}` : ''}`);
  },

  scheduleCampaign: ({ campaignId, scheduledAt, timezone }, button) =>
    Api.post('/email/scheduleCampaign', { campaignId, scheduledAt, timezone }, button, {
      silent: true
    }),

  cancelCampaign: (campaignId, button) => Api.post('/email/cancelCampaign', { campaignId }, button),

  /**
   * "Send Now" — five minutes from now, in fact.
   *
   * The delay is the safeguard, not an implementation detail to hide: a campaign cannot be un-sent
   * and cancelling is refused once sending begins, so the window is the only chance to stop a
   * mistake. The response carries `cancellableUntil` and the screen shows it.
   */
  sendCampaignNow: (campaignId, button) =>
    Api.post('/email/sendCampaignNow', { campaignId }, button, { silent: true }),

  /**
   * One proof to the operator's own address.
   *
   * `{ silent: true }` so the 429 can be rendered with its `resetsAt` inline rather than as a bare
   * toast — "you have sent 20 in the last hour" is only useful with the time it resets.
   */
  testSendCampaign: (campaignId, button) =>
    Api.post('/email/testSendCampaign', { campaignId }, button, { silent: true }),

  duplicateCampaign: (campaignId, name, button) =>
    Api.post('/email/duplicateCampaign', { campaignId, name }, button),

  /** One call for both directions. Archiving never stops a send. */
  setCampaignArchived: (campaignId, archived, button) =>
    Api.post('/email/archiveCampaign', { campaignId, archived }, button),

  // --- Recurrence ---
  setRecurrence: (payload, button) =>
    Api.post('/email/setRecurrence', payload, button, { silent: true }),

  stopRecurrence: (campaignId, button) => Api.post('/email/stopRecurrence', { campaignId }, button),

  // --- Observability ---
  getCampaignStats: (campaignId) =>
    Api.get(`/email/getCampaignStats?campaignId=${encodeURIComponent(campaignId)}`),

  listSends: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, value);
    });
    return Api.get(`/email/listSends?${query.toString()}`);
  },

  // --- Policy ---
  getFatiguePolicy: () => Api.get('/email/getFatiguePolicy'),

  /*
    The hero banner's wording, per language.

    Read is not silent — this one is a page load, and an operator who cannot see the current wording
    needs to be told why rather than shown four empty boxes. The write is silent because the page
    states its own outcome beside the button.
  */
  getBannerCopy: () => Api.get('/email/getBannerCopy'),

  updateBannerCopy: (payload, button) =>
    Api.post('/email/updateBannerCopy', payload, button, { silent: true }),

  updateFatiguePolicy: (payload, button) =>
    Api.post('/email/updateFatiguePolicy', payload, button, { silent: true }),

  /*
    Maintenance, not policy.

    Silent because the result is a count and a list of unresolved country names, which belong beside
    the button that produced them rather than in a toast that is gone before they can be read.
  */
  backfillCountryCodes: (button) =>
    Api.post('/email/backfillCountryCodes', {}, button, { silent: true })
};

export default emailApi;
